<script setup lang="ts">
import type {
  TypeCallToActionSkeleton,
  TypeCallToActionWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { CustomElementsDependencies } from '~/types/custom-elements'
import { useHost } from 'vue'
import CallToAction from '~/components/CallToAction.vue'
import resolveParentEntryTitleFromElement from '~/utils/resolveParentEntryTitleFromElement'

// without below, vue will warn about extraneous attributes when passing data-* attributes to the component
defineOptions({
  inheritAttrs: false,
})

declare global {
  interface Window {
    __WPN_CUSTOM_ELEMENTS__?: CustomElementsDependencies
  }
}

const props = defineProps<{
  entry: string
  dataLayer?: Record<string, unknown>
  link?: string
}>()

const host = useHost()
const cta = ref<TypeCallToActionWithoutUnresolvableLinksResponse>()

type DataLayerObject = Record<string, unknown>

function isPlainObject(value: unknown): value is DataLayerObject {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.prototype.toString.call(value) === '[object Object]'
  )
}

function mergeObject(
  base: DataLayerObject | undefined,
  override: DataLayerObject | undefined,
): DataLayerObject | undefined {
  if (!isPlainObject(base)) return override
  if (!isPlainObject(override)) return base

  const merged: DataLayerObject = { ...base }

  for (const [key, overrideValue] of Object.entries(override)) {
    const baseValue = merged[key]
    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      merged[key] = mergeObject(baseValue, overrideValue)
    } else {
      merged[key] = overrideValue
    }
  }

  return merged
}

function setNestedValue(
  target: DataLayerObject,
  path: string[],
  value: string,
) {
  let current = target

  path.forEach((segment, index) => {
    if (!segment) return

    if (index === path.length - 1) {
      current[segment] = value
      return
    }

    const nextValue = current[segment]
    if (!isPlainObject(nextValue)) {
      current[segment] = {}
    }

    current = current[segment] as DataLayerObject
  })
}

function getAttributeDataLayer(): DataLayerObject | undefined {
  if (!host) return undefined

  const dataLayer: DataLayerObject = {}

  for (const attribute of Array.from(host.attributes)) {
    if (attribute.name === 'data-event') {
      dataLayer.event = attribute.value
      continue
    }

    if (attribute.name === 'data-event-name') {
      dataLayer.eventName = attribute.value
      continue
    }

    if (!attribute.name.startsWith('data-event-params-')) continue

    const path = attribute.name
      .slice('data-event-params-'.length)
      .split('-')
      .filter(Boolean)

    if (!path.length) continue

    if (!isPlainObject(dataLayer.eventParams)) {
      dataLayer.eventParams = {}
    }

    setNestedValue(
      dataLayer.eventParams as DataLayerObject,
      path,
      attribute.value,
    )
  }

  return Object.keys(dataLayer).length ? dataLayer : undefined
}

const inlineDataLayer = computed(() => {
  return mergeObject(
    getAttributeDataLayer(),
    isPlainObject(props.dataLayer) ? props.dataLayer : undefined,
  )
})

const parentEntryTitle = computed(() => {
  return resolveParentEntryTitleFromElement(host ?? null)
})

const slotLabel = computed(() => {
  const text = host?.textContent?.trim()
  return text || undefined
})

async function loadCallToAction() {
  cta.value = undefined

  if (!props.entry || !window.__WPN_CUSTOM_ELEMENTS__) return

  try {
    const deps = window.__WPN_CUSTOM_ELEMENTS__
    const locale = deps.getLocale()

    const response =
      await deps.client.withoutUnresolvableLinks.getEntries<TypeCallToActionSkeleton>(
        {
          content_type: 'callToAction',
          include: 0,
          limit: 1,
          locale,
          'sys.id': props.entry,
        },
      )

    cta.value = response.items[0]
  } catch (error) {
    console.warn('[wpn-cta] failed to load entry', {
      entry: props.entry,
      error,
    })
  }
}

watch(
  () => props.entry,
  () => {
    loadCallToAction()
  },
  { immediate: true },
)
</script>

<template>
  <CallToAction
    v-if="cta"
    :cta="cta"
    :data-layer="inlineDataLayer"
    :parent-entry-title="parentEntryTitle"
    :label="slotLabel"
    :link="link"
    native-link
  />
  <slot v-else />
</template>
